import React from 'react';
import { Box, Menu, MenuList, MenuItem, ListItemIcon, ListItemText, ListSubheader, Divider, Typography, SwipeableDrawer } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VoidIcon from '@mui/icons-material/BlockOutlined';
import { EMAIL_STATUS_SHORT_LABELS } from './InvoiceEmailStatusChip';
import { getInvoiceActionState } from './invoiceActionState';

const menuCaptionSx = {
  fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'text.secondary', lineHeight: 'normal', py: 0.5,
};

// The "Email Invoice" item's right-aligned caption — same "sent" fallback
// InvoiceEmailStatusChip uses, but defaulting to "Not sent" instead of
// rendering nothing.
const emailStateCaption = (invoice, t) => {
  const { emailStatus, emailSentAt } = invoice;
  const key = EMAIL_STATUS_SHORT_LABELS[emailStatus] ? emailStatus : (emailSentAt ? 'sent' : null);
  return key ? t(EMAIL_STATUS_SHORT_LABELS[key]) : t('Not sent', { defaultValue: 'Not sent' });
};

// design_handoff_invoice_actions/README.md (Step 10) + design_handoff_invoice_detail
// (Step 11): the grouped EMAIL / PAYMENT / DOCUMENTS / destructive ⋯ menu, shared
// between InvoiceRow and InvoiceDetailDialog so they can never drift apart.
// `variant="drawer"` renders the same items inside a mobile SwipeableDrawer
// (48px rows, drag handle, Cancel) instead of a desktop Menu.
const InvoiceActionMenu = ({
  t, invoice, open, onClose, anchorEl, variant = 'menu',
  onSendEmail, onRemind, onMarkPaid, onDownloadPdf, onPrintLabel, onDownloadLabel,
  onEdit, onVoid, onDelete, extraItems,
}) => {
  const { isDraft, isIssued, isPaid, canDownloadPdf, canUseShippingLabel } = getInvoiceActionState(invoice);
  const emailCaption = emailStateCaption(invoice, t);
  const runAction = (action) => { action(invoice); onClose(); };
  const isDrawer = variant === 'drawer';

  const items = (
    <>
      {(isIssued || isPaid) && [
        <ListSubheader key="email-header" disableSticky sx={menuCaptionSx}>{t('Email', { defaultValue: 'Email' })}</ListSubheader>,
        <MenuItem key="send-email" onClick={() => runAction(onSendEmail)}>
          <ListItemIcon><MailOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('Email Invoice', { defaultValue: 'Email Invoice' })}</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{emailCaption}</Typography>
        </MenuItem>,
        isIssued && (
          <MenuItem key="send-reminder" onClick={() => runAction(onRemind)}>
            <ListItemIcon><NotificationsNoneIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('Send reminder', { defaultValue: 'Send reminder' })}</ListItemText>
          </MenuItem>
        ),
      ]}

      {isIssued && (
        <ListSubheader key="payment-header" disableSticky sx={{ ...menuCaptionSx, borderTop: '1px solid', borderColor: 'divider', mt: 0.5 }}>
          {t('Payment', { defaultValue: 'Payment' })}
        </ListSubheader>
      )}
      {isIssued && (
        <MenuItem onClick={() => runAction(onMarkPaid)}>
          <ListItemIcon><PaidIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>{t('Mark paid')}</ListItemText>
        </MenuItem>
      )}

      {canDownloadPdf && (
        <ListSubheader key="documents-header" disableSticky sx={{ ...menuCaptionSx, borderTop: (isIssued || isPaid) ? '1px solid' : 0, borderColor: 'divider', mt: (isIssued || isPaid) ? 0.5 : 0 }}>
          {t('Documents', { defaultValue: 'Documents' })}
        </ListSubheader>
      )}
      {canDownloadPdf && (
        <MenuItem onClick={() => runAction(onDownloadPdf)}>
          <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('Download PDF')}</ListItemText>
        </MenuItem>
      )}
      {canUseShippingLabel && (
        <MenuItem onClick={() => runAction(onPrintLabel)}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('Packing slip')}</ListItemText>
        </MenuItem>
      )}
      {canUseShippingLabel && (
        <MenuItem onClick={() => runAction(onDownloadLabel)}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('Download label')}</ListItemText>
        </MenuItem>
      )}

      {isIssued && <Divider sx={{ my: 0.5 }} />}
      {isDraft && (
        <MenuItem onClick={() => runAction(onEdit)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('Edit')}</ListItemText>
        </MenuItem>
      )}
      {extraItems}
      {isIssued && (
        <MenuItem onClick={() => runAction(onVoid)}>
          <ListItemIcon><VoidIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('Void')}</ListItemText>
        </MenuItem>
      )}
      {(isDraft || isIssued) && (
        <MenuItem onClick={() => runAction(onDelete)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('Delete')}</ListItemText>
        </MenuItem>
      )}
    </>
  );

  if (isDrawer) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 999, bgcolor: 'divider', mx: 'auto', mt: 1.25, mb: 0.5 }} />
        <MenuList sx={{ px: 1, pb: 1, '& .MuiMenuItem-root': { minHeight: 48, borderRadius: 1.5 } }}>
          {items}
        </MenuList>
        <Box sx={{ px: 1.5, pb: 3, pt: 0.5 }}>
          <MenuItem
            onClick={onClose}
            sx={{
              justifyContent: 'center', minHeight: 48, borderRadius: 999,
              border: '1px solid', borderColor: 'divider', bgcolor: 'background.default',
            }}
          >
            {t('Cancel')}
          </MenuItem>
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {items}
    </Menu>
  );
};

export default InvoiceActionMenu;
